import React, { useMemo, useCallback, memo } from 'react';
import JoditEditor from 'jodit-pro-react';
import CONSTANTS from '../../constants/contentCreationConstants';

/**
 * Jodit wrapper. Treat `value` as the *initial* content for a given `resetKey`.
 * Remount (via resetKey) when loading a different question — do not feed live
 * keystrokes back through `value` or the editor will refresh on every parent render.
 */
const QuestionEditor = ({
  value,
  onChange,
  placeholder,
  readOnly = false,
  editorHeight = 400,
  autoGrow = false,
  resetKey = 'default',
}) => {
  const initialValue = value === CONSTANTS.DEFAULT_QN ? '' : (value || '');

  const config = useMemo(
    () => ({
      placeholder: placeholder || 'Enter your question...',
      height: autoGrow ? 'auto' : editorHeight,
      minHeight: autoGrow ? editorHeight : undefined,
      toolbarSticky: false,
      toolbar: !readOnly,
      readonly: readOnly,
      buttons: [
        'bold', 'italic', 'underline', 'strikethrough',
        '|', 'ul', 'ol', '|', 'link', 'unlink',
        '|', 'align', '|', 'undo', 'redo',
      ],
      removeButtons: ['brush', 'font', 'fontsize', 'paragraph'],
      showPlaceholder: true,
      disablePlugins: ['mobile'],
      enter: 'br',
      showCharsCounter: false,
      showWordsCounter: false,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: 'insert_as_html',
      processPasteHTML: false,
      spellcheck: false,
      cleanHTML: false,
      events: {
        beforePaste: function () {
          return true;
        },
      },
    }),
    [placeholder, readOnly, editorHeight, autoGrow]
  );

  const handleChange = useCallback(
    (newValue) => {
      if (readOnly) return;
      sessionStorage.setItem('currentQuestionContent', newValue);
      if (typeof onChange === 'function') {
        onChange(newValue);
      }
    },
    [readOnly, onChange]
  );

  return (
    <JoditEditor
      key={String(resetKey)}
      value={initialValue}
      config={config}
      onChange={handleChange}
      style={{
        border: '2px solid #e5e7eb',
        borderRadius: '10px',
      }}
    />
  );
};

export default memo(QuestionEditor);
